<script lang="ts" setup>
import { onMounted, ref } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useCompanyStore } from '@Stores/company.store';
import { useLocationStore } from '@Stores/location.store';
import useCompanyImageBaseUrl from '@Use/useCompanyImageBaseUrl';
import { LINKS } from "@Constants/links.constants";

import { ShowcaseLayout } from '@Shared';
import ShowcaseImage from '@Components/ShowcaseImage.vue';
import { markRecipientAsStartedApi, getRecipientAndDocumentStatusByRecipientTokenApi } from '@Api/esignature.api';
import { RequestStatusType } from '@Enums/requestStatusType.enum';
import { RecipientStatusType, resolveRecipientStatus } from '@Enums/recipientStatusType.enum';
import { getRequestStatus } from '@Types/documentSigningStatus.type';
import { useSigningRequestStore } from '@Stores/signingRequest.store';
import { getSigningRequestRedirect } from '@Router/signingRequestRedirect';

const companyImageBaseUrl = useCompanyImageBaseUrl();
const router = useRouter();
const route = useRoute();
const companyStore = useCompanyStore();
const locationStore = useLocationStore();
const signingRequestStore = useSigningRequestStore();
const recipientToken = route.params.token as string;
const isCheckingStatus = ref(true);
const isRecipientSent = ref(false);
const requestCanMarkRecipientAsStarted = ref(false);

console.log('[Esign MFE] Navigating Get Started');

onMounted(async () => {

    try {
        const recipientToken = route.params.token as string;
        if (recipientToken) {
            await signingRequestStore.setValidatedToken(recipientToken);
            console.log("[Esign MFE] Token saved from URL params");
        }
        const status = await getRecipientAndDocumentStatusByRecipientTokenApi(recipientToken);
        const docStatus = getRequestStatus(status);
        if (docStatus === undefined) {
            console.error('[Esign MFE] Could not resolve document status');
            return;
        }
        const recipientStatus = resolveRecipientStatus(status.recipientStatusType);

        isRecipientSent.value = recipientStatus === RecipientStatusType.Sent;
        requestCanMarkRecipientAsStarted.value = docStatus === RequestStatusType.Sent ||
            docStatus === RequestStatusType.Started ||
            docStatus === RequestStatusType.InProgress;

        console.log('[Esign MFE] Document status:', status);

        const redirect = getSigningRequestRedirect(docStatus, recipientStatus);
        if (redirect && redirect !== 'not-found') {
            await router.push({ name: redirect });
            return;
        }

    } catch (error) {
        console.error('[Esign MFE] Failed to check document status:', error);
    } finally {
        isCheckingStatus.value = false;
    }
});

const continueFlow = async () => {
    if (requestCanMarkRecipientAsStarted.value && isRecipientSent.value) {
        await markRecipientAsStartedApi(recipientToken);
    }
    router.push({ name: 'whats-next' });
};
</script>

<template>  
    <app-page-loader v-if="isCheckingStatus" />
    <showcase-layout 
        v-else
        :company="companyStore.company"
        :brand="companyStore.brand"
        :location="locationStore.location"
        :company-image-base-url="companyImageBaseUrl"
        show-feature-logo
        show-powered-by>
    
        <div class="text-center">
            
            <img 
                class="house-candy"
                :src="'/images/esignature/secure-signing.svg'" />

           <h1>Secure Signing.</h1>
            <div class="highlight">Made Simple.</div>

            <div class="legal">
                By continuing, you agree to our 
                <app-link type="external" :link-url="LINKS.CertifID.TermsOfUse">Terms of Use</app-link> and 
                <app-link type="external" :link-url="LINKS.CertifID.PrivacyPolicy">Privacy Policy</app-link>.
            </div>

            <app-button
                expanded
                size="large"
                @onClick="continueFlow"
                class="get-started-btn">
                    Get Started
            </app-button>
        </div>
        <template #branded>
            <showcase-image />
        </template>
    </showcase-layout>        

    
</template>

<style lang="scss" scoped>
@use 'certifid-v2-client-hub-styles' as *;

.text-center {
    text-align: center;
}

.house-candy {
    margin-bottom: 0.69rem;
    max-width: 11.125rem;
    max-height: 11.5625rem;

    @include desktop {
        display: none;
    }
}

.highlight {
    color: $theme-primary;
    font-size: 1.4375rem;
    font-style: normal;
    font-weight: 700;
    line-height: 150%;
    margin-bottom: 2rem;
}

.get-started-btn {
    margin: 1.5rem auto;

    @include desktop {
        max-width: 15rem;
    }
}

.legal {
    font-size: 0.75rem;
    font-style: normal;
    font-weight: 400;
    line-height: 150%;
}


</style>